import React, { useEffect } from 'react';
import { GripVertical, Pencil } from 'lucide-react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { MenuData, MenuCategory, MenuItem as MenuItemType } from '../../types';
import { SortableItem } from './SortableItem';

// Helper for Image Resolution
const resolveImageUrl = (raw: string | undefined | null) => {
  if (!raw) return null;
  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('/api/assets')) return raw;
  if (raw.includes('.r2.dev/')) return `/api/assets?key=${encodeURIComponent(raw.split('.r2.dev/')[1])}`;
  if (raw.includes('sterlingops-assets/')) return `/api/assets?key=${encodeURIComponent(raw.split('sterlingops-assets/')[1])}`;
  if (raw.startsWith('uploads/')) return `/api/assets?key=${encodeURIComponent(raw)}`;
  if (raw.startsWith('http')) return raw;
  return `data:image/png;base64,${raw}`;
};

interface MenuCanvasProps {
  data: MenuData;
  language: 'en' | 'ar';
  printRef: React.RefObject<HTMLDivElement>;
  onCategoryClick?: (id: string) => void;
  onItemClick?: (id: string) => void;
  focusTarget?: { id: string; ts: number } | null;
  currency: string;
}

// --- Visual Components ---

const MenuItem = ({ item, language, currency, onClick }: { item: MenuItemType, language: 'en' | 'ar', currency: string, onClick?: () => void }) => {
  const isArabic = language === 'ar';
  const choiceGroups = Array.isArray(item.choiceGroups) ? item.choiceGroups : [];
  
  return (
    <div 
        onClick={onClick}
        className={`py-6 relative group ${isArabic ? 'flex-row-reverse text-right' : 'flex-row text-left'} flex gap-6 cursor-pointer hover:bg-slate-50/50 rounded-lg transition-colors px-2 -mx-2`}
    >
      
      {/* Item Image */}
      <div className="shrink-0 w-32 h-32 rounded-lg overflow-hidden shadow-sm border border-slate-100 bg-slate-50 self-start relative">
        {item.image ? (
          <img 
            src={resolveImageUrl(item.image) || ''} 
            alt={item.name.en} 
            className="w-full h-full object-cover"
            onError={(e) => { 
              (e.target as HTMLImageElement).style.display = 'none'; 
              const placeholder = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
              if (placeholder) placeholder.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`absolute inset-0 flex items-center justify-center text-[11px] text-slate-400 ${item.image ? 'hidden' : ''} bg-slate-100`}>
          No image
        </div>
      </div>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Name and Price Line */}
        <div className={`flex items-baseline justify-between mb-2 ${isArabic ? 'flex-row-reverse' : 'flex-row'}`}>
            <h4 className={`text-lg font-bold text-slate-900 leading-tight ${isArabic ? 'font-arabic' : 'font-sans tracking-tight'}`}>
              {isArabic ? (item.name.ar || item.name.en) : item.name.en}
            </h4>
            
            <div className={`flex items-baseline gap-1 shrink-0 ${isArabic ? 'mr-6 flex-row-reverse' : 'ml-6'}`}>
                 <span className="text-lg font-bold text-slate-900">
                    {item.price.toFixed(2)}
                 </span>
                 <span className="text-sm font-normal text-slate-400 ml-1">{currency}</span>
            </div>
        </div>
        
        {/* Description */}
        <p className={`text-sm text-slate-600 leading-relaxed max-w-prose ${isArabic ? 'font-arabic' : 'font-serif italic'}`}>
          {isArabic ? (item.description.ar || item.description.en) : item.description.en}
        </p>

        {/* Choice Groups Display */}
        {choiceGroups.length > 0 && (
            <div className="mt-6 space-y-6">
            {choiceGroups.map(group => {
              const options = Array.isArray(group.options) ? group.options : [];
              return (
                    <div key={group.id} className="text-sm">
                        <div className={`flex items-center gap-2 mb-3 ${isArabic ? 'flex-row-reverse' : 'flex-row'}`}>
                            <span className="font-bold text-emerald-900 uppercase tracking-widest text-xs">
                                {isArabic ? (group.name.ar || group.name.en) : group.name.en}
                            </span>
                        </div>
                        <div className={`space-y-2 ${isArabic ? 'text-right' : 'text-left'}`}>
                  {options.map(opt => (
                                <div key={opt.id} className={`flex items-center justify-between text-slate-600 ${isArabic ? 'flex-row-reverse' : 'flex-row'}`}>
                                    <div className={`flex items-center gap-2 ${isArabic ? 'flex-row-reverse' : 'flex-row'}`}>
                                        <div className="w-1 h-1 rounded-full bg-slate-300"></div>
                                        <span>{isArabic ? (opt.name.ar || opt.name.en) : opt.name.en}</span>
                                    </div>
                                    {opt.priceModifier ? (
                                        <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md text-[10px] font-bold border border-slate-200">
                                            +{currency} {opt.priceModifier.toFixed(2)}
                                        </span>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </div>
                    );
                  })}
            </div>
        )}
      </div>

      {/* Drag Handle Overlay */}
      <div className={`absolute top-6 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing text-slate-300 print:hidden transition-opacity ${isArabic ? '-right-10' : '-left-10'}`}>
        <GripVertical size={24} />
      </div>
    </div>
  );
};

const CategoryBlock = ({ category, language, onClick, onItemClick, currency }: { category: MenuCategory, language: 'en' | 'ar', onClick?: () => void, onItemClick?: (id: string) => void, currency: string }) => {
  const isArabic = language === 'ar';
  const items = Array.isArray(category.items) ? category.items : [];

  const categories = Array.isArray(data.categories) ? data.categories : [];

  return (
    <div 
        id={`canvas-cat-${category.id}`}
        className="mb-12 break-inside-avoid relative group"
    >
       {/* Category Title */}
       <div 
          onClick={onClick}
          className={`flex items-center mb-8 pb-3 border-b-2 border-slate-900 cursor-pointer hover:bg-slate-50/50 rounded transition-colors ${isArabic ? 'flex-row-reverse' : 'flex-row'}`}
          title="Click to edit category"
       >
         <h3 className={`text-xl font-black uppercase tracking-widest text-slate-900 flex-1 ${isArabic ? 'font-arabic text-right' : 'font-sans text-left'}`}>
            {isArabic ? (category.name.ar || category.name.en) : category.name.en}
         </h3>
         
         <div className={`opacity-0 group-hover:opacity-100 text-slate-400 px-3 print:hidden transition-opacity`}>
             <Pencil size={16} />
         </div>

         <div className={`absolute top-2 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing text-slate-300 print:hidden ${isArabic ? '-right-10' : '-left-10'}`}>
            <GripVertical size={24} />
         </div>
       </div>

       <div className="space-y-4">
         <SortableContext 
            id={category.id} 
            items={items.map(i => i.id)} 
            strategy={verticalListSortingStrategy}
         >
            {items.length === 0 && (
                <div className="py-8 text-center border-2 border-dashed border-slate-200 rounded-lg text-slate-400 italic">
                    {isArabic ? 'لا توجد عناصر في هذا القسم' : 'No items in this category'}
                </div>
            )}
            {items.map(item => (
                <SortableItem key={item.id} id={item.id}>
                    <MenuItem 
                        item={item} 
                        language={language} 
                        currency={currency} 
                        onClick={() => onItemClick?.(item.id)}
                    />
                </SortableItem>
            ))}
         </SortableContext>
       </div>
    </div>
  );
};

export const MenuCanvas: React.FC<MenuCanvasProps> = ({ data, language, printRef, onCategoryClick, onItemClick, focusTarget, currency }) => {
  const isArabic = language === 'ar';

  useEffect(() => {
    if (focusTarget) {
      const el = document.getElementById(`canvas-cat-${focusTarget.id}`);
      if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [focusTarget]);

  return (
    <div className="h-full bg-slate-100 overflow-y-auto no-scrollbar relative">
      <div 
        id="printable-canvas"
        ref={printRef}
        className={`min-h-full bg-white p-6 md:p-10 lg:p-12 flex flex-col transition-all duration-300 ${isArabic ? 'dir-rtl' : 'dir-ltr'} relative`}
        dir={isArabic ? 'rtl' : 'ltr'}
      >
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")'}}></div>

        <div className="max-w-5xl mx-auto w-full flex-1 flex flex-col">
            <header className="text-center mb-14 relative z-10">
                <h1 className={`text-4xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight ${isArabic ? 'font-arabic' : 'font-sans'}`}>
                    {isArabic ? (data.title.ar || data.title.en) : data.title.en}
                </h1>
                <div className="w-20 h-1 bg-slate-900 mx-auto"></div>
            </header>

            <div className="flex-1 relative z-10">
                <SortableContext 
                    id="root"
                    items={categories.map(c => c.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {categories.map(category => (
                        <SortableItem key={category.id} id={category.id}>
                            <CategoryBlock 
                                category={category} 
                                language={language} 
                                onClick={() => onCategoryClick?.(category.id)}
                                onItemClick={onItemClick}
                                currency={currency}
                            />
                        </SortableItem>
                    ))}
                </SortableContext>
            </div>

            <footer className="mt-20 text-center text-slate-300 text-xs uppercase tracking-widest relative z-10 pb-8">
                Menu created with BistroBuilder
            </footer>
        </div>
      </div>
    </div>
  );
};
